import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import UserAuthStore from "../../store/userStore";
import ChatStore from "../../store/chatStore";
import useSessionIdStore from "../../store/useSessionIdStore";
import api from "../../api";
import useFeedback from "../../hooks/useFeedback";
import logger from "../../services/Logger";
import { ROUTE_HELPERS } from "../../router/routes";
import { broadcastInvalidate } from "../../api/broadcastSync";
import QUERYKEYS from "../../api/queryKeys";

/**
 * Custom hook for consistent spark deletion across the application.
 * Provides optimistic updates and graceful error handling.
 * Always navigates to fresh start (empty state) when deleting the current spark.
 * 
 * @param {object|null} space - Space/Spectra context object (null for regular chats)
 * @returns {{deleteSpark: (sparkId: string, chat?: object) => Promise<void>}}
 * 
 * @example
 * // In a Spectra context
 * const { deleteSpark } = useSparkDeletion(space);
 * deleteSpark(sparkId);
 * 
 * @example
 * // In regular chat context
 * const { deleteSpark } = useSparkDeletion(null);
 * deleteSpark(sparkId, chat);
 */
export const useSparkDeletion = (space) => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const deleteSpark = useCallback(async (sparkId, chat) => {
    const { user, orgId } = UserAuthStore.getState();
    const activeChat = ChatStore.getState().activeChat;
    const session_id = useSessionIdStore.getState().session_id;

    if (!sparkId || !user?.uid || !orgId) {
      logger.error('useSparkDeletion', 'Missing required parameters', { sparkId, userId: user?.uid, orgId });
      return;
    }

    // Determine query key based on context (spectra vs regular chat)
    const queryKey = space?.space_id 
      ? ['spectra-sparks', space.space_id, orgId, user.uid, space.owner_id]
      : [QUERYKEYS.CHATS, orgId, user.uid];
    
    // Get current data for rollback
    const previousData = queryClient.getQueryData(queryKey);
    
    // Determine if we're deleting the currently active spark
    const isDeletingActiveSpark = activeChat === sparkId;

    // Cancel any in-flight refetch for this key so it cannot overwrite the
    // optimistic removal below. Without this, a refetch already in flight
    // (triggered by a Firestore listener or stale-time expiry) would
    // repopulate the cache with the spark still present.
    await queryClient.cancelQueries({ queryKey });

    // OPTIMISTIC UPDATE: Remove spark from cache immediately
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;
      
      // Handle spectra-sparks nested structure
      if (oldData.sparks && Array.isArray(oldData.sparks)) {
        return {
          ...oldData,
          sparks: oldData.sparks.filter(s => s.uid !== sparkId)
        };
      }
      
      // Handle regular chats array
      if (Array.isArray(oldData)) {
        return oldData.filter(s => s.uid !== sparkId);
      }
      
      return oldData;
    });

    // Navigate away immediately if deleting the active spark — don't wait for API
    if (isDeletingActiveSpark) {
      if (space?.space_id) {
        navigate(ROUTE_HELPERS.spectra.chat(space.space_id));
      } else {
        navigate('/chat');
      }
    }

    // Determine owner_id for backend API
    const owner_id = space?.owner_id || chat?.owner_id || user.uid;
    
    // Backend API call (async, UI already updated)
    try {
      const response = await api({
        method: "POST",
        url: "/spark/delete_spark",
        data: {
          chat_id: sparkId,
          org_id: orgId,
          user_id: owner_id,
          by: user.uid,
          session_id: session_id,
        },
      });

      if (response.data?.success) {
        broadcastInvalidate(queryKey);
        logger.info('useSparkDeletion', 'Spark deleted successfully', { 
          sparkId, 
          isDeletingActiveSpark,
          spaceId: space?.space_id 
        });
      } else {
        // Backend returned failure, rollback
        queryClient.setQueryData(queryKey, previousData);
        const errorMsg = response.data?.error || "Failed to delete spark";
        logger.error('useSparkDeletion', 'Backend returned failure', { 
          error: errorMsg, 
          sparkId, 
          orgId 
        });
        showFeedback(errorMsg, "error");
      }
    } catch (error) {
      // Exception thrown, rollback
      queryClient.setQueryData(queryKey, previousData);
      logger.error('useSparkDeletion', 'Exception during deletion', { 
        error: error?.message, 
        sparkId, 
        orgId 
      });
      showFeedback("Failed to delete spark. Please try again.", "error");
    }
  }, [space, navigate, queryClient, showFeedback]);

  return { deleteSpark };
};
